/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from '@/components/link';
import type { ShopperSearch } from '@/scapi';
import { createProductUrl, getTileImagesForColor } from '@/lib/product/product-utils';
import { useDynamicImageContext } from '@/providers/dynamic-image';
import { ProductImage } from './product-image';
import ImageNavArrows from '@/components/image-nav-arrows';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

// Default viewType used when the search-image role is unconfigured. Aligned
// with `config.server.ts` `search.products.images.tile` so the SCAPI
// `imgTypes` filter and this lookup stay in lock-step. `'grid'` matches
// Ashley's catalog declaration in `int_ashley_jobs/.../productImageMapper.js`
// for the PLP-tile-sized product photo (`large` is the PDP-sized photo).
const DEFAULT_TILE_VIEW_TYPE = 'grid';

// Ordered fallback chain consulted for swatch-hover variant images. The first
// entry is the configured tile viewType (default state, master photo); the
// remaining entries are tried only when a swatch is hovered and the primary
// viewType has no color-specific image group for that color. Ashley's catalog
// authors color-scoped image groups exclusively under `large`
// (`int_ashley_jobs/.../productImageMapper.js` writes
// `<image-group view-type="large">` per color), while `grid` carries only the
// master-level group. Without this chain, swatch-hover would leave the tile
// image unchanged because no `grid` color group exists.
const SWATCH_HOVER_FALLBACK_VIEW_TYPES = ['large'] as const;

interface ProductImageContainerProps {
    product: ShopperSearch.schemas['ProductSearchHit'];
    selectedColorValue?: string | null;
    className?: string;
    handleProductClick?: (product: ShopperSearch.schemas['ProductSearchHit']) => void;
    /** Image aspect ratio (width/height). If provided, calculates height based on viewport width. Defaults to 1 (square) */
    imgAspectRatio?: number;
    /** Show prev/next navigation arrows when multiple images are available */
    showNavigationArrows?: boolean;
    /**
     * Aspect ratio (width / height) forwarded to `ProductImage` for srcset height derivation.
     * Defaults to 4/3 — matches the standard product image crop used across Scene7 assets.
     */
    aspectRatio?: number;
    /**
     * Optional rollover ("hover") image URL. When provided, a `<picture>`-backed overlay
     * is rendered inside the existing anchor — positioned absolutely over the primary image
     * and cross-fading in on hover. Keeping it inside the anchor removes the need for a
     * separate click-overlay `<Link>` in the parent tile.
     */
    rolloverImageUrl?: string;
}

const ProductImageContainer = ({
    product,
    selectedColorValue = null,
    className,
    handleProductClick,
    imgAspectRatio = 1,
    showNavigationArrows = false,
    aspectRatio = imgAspectRatio,
    rolloverImageUrl,
}: ProductImageContainerProps) => {
    const { t } = useTranslation('product');
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    // Source the tile viewType from `search.products.images.tile` so the
    // SCAPI `imgTypes` filter (set in `fetchSearchProducts`) and this lookup
    // resolve the same image group. Hardcoding a viewType here that the
    // catalog doesn't populate (e.g. the SFRA default `'medium'` against
    // Ashley's `large/swatch/rollover/grid` view-types) yields an empty
    // result, the caller then falls back to `product.image`, and SCAPI's
    // `hit.image` — selected from whichever filter type matches first — can
    // be a swatch URL, surfacing as tiny color/material swatches in the PLP
    // tile slot. Ashley's catalog populates `grid` for the PLP-tile photo
    // (and `large` for the PDP photo).
    const config = useConfig();
    const tileViewType = config?.search?.products?.images?.tile ?? DEFAULT_TILE_VIEW_TYPE;

    // Build the viewType fallback chain once per render. `tileViewType` first
    // (default tile photo when no swatch is hovered), then the secondary
    // viewType(s) used to recover the variant photo on swatch-hover when the
    // primary viewType has no color-scoped group — see
    // `SWATCH_HOVER_FALLBACK_VIEW_TYPES` and `getTileImagesForColor` for the
    // matching rationale.
    const viewTypeChain = useMemo(
        () => [tileViewType, ...SWATCH_HOVER_FALLBACK_VIEW_TYPES.filter((vt) => vt !== tileViewType)],
        [tileViewType]
    );

    // Resolve the visible images: master photo at the configured viewType
    // when no swatch is hovered, color-specific variant photo (across the
    // viewType chain) when one is.
    const allImages = useMemo(
        () => getTileImagesForColor(product, selectedColorValue, viewTypeChain),
        [product, selectedColorValue, viewTypeChain]
    );

    const currentImage = allImages[selectedImageIndex] ?? allImages[0] ?? product.image;
    const currentImageUrl = currentImage?.disBaseLink || currentImage?.link;
    const imageAltFallback = product.productName || t('imageAlt') || 'Product Image';

    // Report the image URL to the dynamic image context, if available
    const imageContext = useDynamicImageContext();
    currentImageUrl && imageContext?.addSource(currentImageUrl);

    const handleClick = useCallback(() => {
        handleProductClick?.(product);
    }, [handleProductClick, product]);

    // When a non-square aspect ratio is requested, apply it via the native CSS
    // `aspect-ratio` property. The legacy padding-bottom percentage trick is not
    // used here because it conflicts with the native property and collapses the
    // image height to zero.
    const heightStyle = imgAspectRatio !== 1 ? { aspectRatio: `${imgAspectRatio}` } : {};

    return (
        <div
            className={`${showNavigationArrows ? 'group/image ' : ''}relative overflow-hidden bg-secondary/20 flex flex-col ${
                imgAspectRatio === 1 ? 'aspect-square' : ''
            } ${className || ''}`}
            style={heightStyle}>
            {/* Product Image */}
            <Link
                to={createProductUrl(product.productId, selectedColorValue, 'color', null, product.productName)}
                onClick={handleClick}
                className="block h-full w-full"
                aria-label={t('viewProductAriaLabel', { productName: imageAltFallback }) || imageAltFallback}>
                {currentImageUrl ? (
                    <ProductImage
                        src={currentImageUrl}
                        alt={currentImage?.alt || imageAltFallback}
                        className="h-full w-full transition-all duration-200"
                        imageProps={{ className: 'h-full w-full' }}
                        widths={imageContext?.widths}
                        aspectRatio={aspectRatio}
                    />
                ) : (
                    <div className="h-full w-full bg-secondary/20" aria-hidden />
                )}
                {rolloverImageUrl && (
                    <ProductImage
                        src={rolloverImageUrl}
                        alt=""
                        className="pointer-events-none absolute inset-0"
                        imageProps={{
                            'aria-hidden': true,
                            loading: 'lazy',
                            className:
                                'bg-card mx-auto h-full w-full max-w-full opacity-0 transition-opacity duration-300 group-hover/tile-image:opacity-100',
                        }}
                        widths={imageContext?.widths}
                        aspectRatio={aspectRatio}
                    />
                )}
            </Link>
            {/* Navigation Arrows - visible on hover */}
            {showNavigationArrows && allImages.length > 1 && (
                <ImageNavArrows
                    imageCount={allImages.length}
                    onIndexChange={setSelectedImageIndex}
                    className="opacity-0 group-hover/image:opacity-100 transition-opacity"
                />
            )}
        </div>
    );
};

export { ProductImageContainer };
